import { Button, confirm, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle, Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { ProjectListItem } from "@/modules/management/project/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { managementProjectApi } from "../api/client";
import { useProjectsQuery } from "../api/queries";
import {
  ProjectListTour,
  useProjectListTour,
} from "../tour/ProjectListTour";
import { ProjectFilter, type ProjectSearchParams } from "../ui/ProjectFilter";
import { ProjectTable } from "../ui/ProjectTable";

const PAGE_SIZE = 20;

export function ProjectListPage() {
  const nav = useNavigate({ from: "/app/management/projects" });
  const search = useSearch({
    from: "/app/management/projects",
  }) as ProjectSearchParams & {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const sortBy = search.sort?.[0]?.field ?? "name";
  const sortOrder = (search.sort?.[0]?.dir ?? "asc") as "asc" | "desc";
  const offset = search.offset ?? 0;
  const limit = search.limit ?? PAGE_SIZE;

  const query = useMemo(
    () => ({
      offset,
      limit,
      sort: search.sort,
      status: search.status,
      level: search.level,
      departmentId: search.departmentId,
      positionId: search.positionId,
      responsibleUserId: search.responsibleUserId,
      startDateFrom: search.startDateFrom,
      startDateTo: search.startDateTo,
      endDateFrom: search.endDateFrom,
      endDateTo: search.endDateTo,
      search: search.search,
    }),
    [
      offset,
      limit,
      search.sort,
      search.status,
      search.level,
      search.departmentId,
      search.positionId,
      search.responsibleUserId,
      search.startDateFrom,
      search.startDateTo,
      search.endDateFrom,
      search.endDateTo,
      search.search,
    ],
  );

  const listQuery = useProjectsQuery(query);
  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;

  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canCreate = permissions.has(Permissions.mgmt_project.create);
  const canRead =
    permissions.has(Permissions.mgmt_project.read) ||
    permissions.has(Permissions.mgmt_project.list);
  const canUpdate = permissions.has(Permissions.mgmt_project.update);
  const canDelete = permissions.has(Permissions.mgmt_project.delete);

  const toSearch = useCallback(
    (overrides: Partial<typeof search>) => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? PAGE_SIZE,
      sort: search.sort,
      status: search.status,
      level: search.level,
      departmentId: search.departmentId,
      positionId: search.positionId,
      responsibleUserId: search.responsibleUserId,
      startDateFrom: search.startDateFrom,
      startDateTo: search.startDateTo,
      endDateFrom: search.endDateFrom,
      endDateTo: search.endDateTo,
      search: search.search,
      ...overrides,
    }),
    [search],
  );

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({ search: toSearch({ offset: newOffset, limit: newLimit }) });
  };

  const handleView = (row: ProjectListItem) => {
    nav({ to: "/app/management/projects/$id", params: { id: row.id } });
  };

  const handleEdit = (row: ProjectListItem) => {
    nav({
      to: "/app/management/projects/$id/edit",
      params: { id: row.id },
    });
  };

  const handleDeleteProject = async (row: ProjectListItem) => {
    const ok = await confirm({
      title: "ລຶບໂຄງການ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບໂຄງການ "${row.name}"? ວຽກຍ່ອຍ ແລະ ຄວາມສຽງທັງໝົດຈະຖືກລຶບຕາມ. ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await managementProjectApi.deleteProject(row.id);
      toast.success("ລຶບໂຄງການສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: ["management", "projects"] });
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ",
      );
    }
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: toSearch({
          offset: 0,
          limit,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
        }),
      });
    },
    [nav, toSearch, limit],
  );

  const handleNewProject = () => {
    nav({ to: "/app/management/projects/new" });
  };

  const responsibleOptions = useMemo(() => {
    const withUser = data.filter(
      (p): p is ProjectListItem & { responsibleUserId: string } =>
        Boolean(p.responsibleUserId),
    );
    const seen = new Set<string>();
    return withUser
      .filter((p) => {
        if (seen.has(p.responsibleUserId)) return false;
        seen.add(p.responsibleUserId);
        return true;
      })
      .map((p) => ({
        value: p.responsibleUserId,
        label: p.responsibleUserName ?? p.responsibleUserId,
      }));
  }, [data]);

  const tour = useProjectListTour();

  return (
    <>
      <Header />
      <Main>
        <ProjectListTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between"
          data-tourid="project-toolbar"
        >
          <div className="flex items-center gap-2">
            <div>
              <h1 className="font-semibold text-2xl">ການຈັດການໂຄງການ</h1>
              <p className="text-muted-foreground text-sm">
                ລາຍການໂຄງການ ແລະ ຕິດຕາມຄວາມຄືບໜ້າ
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
              aria-label="ເລີ່ມທົດລອງ"
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>
          {canCreate && (
            <Button
              variant="default"
              onClick={handleNewProject}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              ເພີ່ມໂຄງການ
            </Button>
          )}
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <ProjectFilter
            searchParams={{ ...search, limit: search.limit ?? PAGE_SIZE }}
            fromRoute="/app/management/projects"
            responsibleOptions={responsibleOptions}
          />
          <div data-tourid="project-table">
            <ProjectTable
            data={data}
            isLoading={listQuery.isLoading}
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            onPaginationChange={handlePaginationChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={handleSortingChange}
            onView={canRead ? handleView : undefined}
            onEdit={canUpdate ? handleEdit : () => {}}
            onDelete={canDelete ? handleDeleteProject : undefined}
          />
          </div>
        </div>
      </Main>
    </>
  );
}
